import { Select } from 'antd';
import { useTranslations } from 'next-intl';
import { FC, useEffect, useState } from 'react';
import {
  AllFiltersWrapper,
  ModalButton,
  ModalButtonSecond,
  ModalButtonWrapper,
  ModalHeader,
  ModalSection,
  SearchType,
  SearchTypeGrid,
  SearchTypesCheck,
  SearchTypesCheckLabel,
} from './styles';

interface ButtonSectionProps {
  onClickApply: () => void;
  onClickClear: (name: string) => void;
  name: string;
}

/*** GENERAL */
export const ButtonSection: FC<ButtonSectionProps> = ({ onClickApply, onClickClear, name }) => {
  const t = useTranslations();
  return (
    <ModalButtonWrapper>
      <ModalButton onClick={onClickApply}>{t('Apply')}</ModalButton>
      <ModalButtonSecond onClick={() => onClickClear(name)}>{t('Clear')}</ModalButtonSecond>
    </ModalButtonWrapper>
  );
};

interface CheckBoxSectionProps {
  array: FilterObject[];
  selectedarray: string[];
  title?: string;
  type: string;
  handleSetCheckbox: (type: string, b: string) => void;
  dataTestId?: string;
}

const CheckBoxSection: FC<CheckBoxSectionProps> = ({
  array,
  selectedarray,
  title,
  type,
  handleSetCheckbox,
  dataTestId,
}) => {
  return (
    <ModalSection>
      <ModalHeader>{title}</ModalHeader>
      {array && (
        <CheckboxSection
          items={array}
          handleSetSelected={handleSetCheckbox}
          selectedArr={selectedarray}
          type={type}
          data-testid={dataTestId}
        />
      )}
    </ModalSection>
  );
};

interface CheckboxSectionProps {
  items: FilterObject[];
  handleSetSelected: (type: string, b: string) => void;
  selectedArr: string[];
  type: string;
}

export const CheckboxSection: FC<CheckboxSectionProps> = ({
  items,
  handleSetSelected,
  selectedArr,
  type,
}) => {
  const t = useTranslations();

  return (
    <SearchTypeGrid>
      {items.map((p) => {
        return (
          <CheckboxSingle
            key={p.value}
            title={t(p.title as any)}
            value={p.value}
            handleSetSelected={handleSetSelected}
            selectedArr={selectedArr}
            type={type}
          />
        );
      })}
    </SearchTypeGrid>
  );
};

interface CheckboxSingleProps {
  title: string;
  value: string;
  handleSetSelected: (type: string, b: string) => void;
  selectedArr: string[];
  type: string;
}

const CheckboxSingle: FC<CheckboxSingleProps> = ({
  title,
  value,
  handleSetSelected,
  selectedArr,
  type,
}) => {
  const [selected, setSelected] = useState(false);

  useEffect(() => {
    selectedArr.includes(value) ? setSelected(true) : setSelected(false);
  }, [selectedArr]);

  const handleSetSelectedSingle = (): void => {
    handleSetSelected(type, value);
  };
  return (
    <SearchType onClick={handleSetSelectedSingle} $selected={selected}>
      <SearchTypesCheck type="checkbox" checked={selected} onChange={handleSetSelectedSingle} />
      <SearchTypesCheckLabel>{title}</SearchTypesCheckLabel>
    </SearchType>
  );
};

interface MultiSelectDropdownProps {
  title: string;
  values: FilterObject[];
  selectedValues: string[];
  handleSetSelected: (type: string, b: string | string[]) => void;
  type: string;
}

const MultiSelectDropdown: FC<MultiSelectDropdownProps> = ({
  title,
  values,
  selectedValues,
  handleSetSelected,
  type,
}) => {
  const formattedValues = values.map((v) => {
    return {
      value: v.value,
      label: v.title,
    };
  });

  return (
    <ModalSection>
      <ModalHeader>{title}</ModalHeader>
      <Select
        style={{ width: '100%' }}
        labelInValue={false}
        mode="multiple"
        labelRender={(l) => l.label?.toString() ?? ''}
        optionRender={(o) => o.data.label.toString()}
        filterOption={(input, o) =>
          (o?.label.toString() ?? '').toLowerCase().includes(input.toLocaleLowerCase())
        }
        onClear={() => handleSetSelected(type, [])}
        value={selectedValues}
        onChange={(value) => handleSetSelected(type, value.join())}
        defaultValue={selectedValues}
        options={formattedValues}
      />
    </ModalSection>
  );
};

interface FilterObject {
  title: string;
  value: string;
}
interface Filter {
  title: string;
  type: string;
  array: FilterObject[];
  selectedArray: string[];
}

/*** ALL FILTERS */
interface AllFiltersSectionProps {
  filters: Filter[];
  handleSetArr: (type: string, b: string | string[]) => void;
  handleSetSelectedFilter: (f: string) => void;
}

export const AllFiltersSection = ({
  filters,
  handleSetArr,
  handleSetSelectedFilter,
}: AllFiltersSectionProps) => {
  return (
    <AllFiltersWrapper>
      {filters.length > 0 &&
        filters.map((filter) => {
          if (filter.type === 'languages') {
            return (
              <MultiSelectDropdown
                title={filter.title}
                values={filter.array}
                type={filter.type}
                selectedValues={filter.selectedArray}
                handleSetSelected={handleSetArr}
              />
            );
          } else {
            return (
              <CheckBoxSection
                array={filter.array}
                selectedarray={filter.selectedArray}
                title={filter.title}
                type={filter.type}
                handleSetCheckbox={handleSetArr}
              />
            );
          }
        })}
    </AllFiltersWrapper>
  );
};
